import { useState, useCallback, useRef } from 'react';
import { useAITextRecognition, type FieldSuggestion, type TextCategory } from './useAITextRecognition';
import { useToast } from '@/hooks/use-toast';

export interface SmartSuggestion extends FieldSuggestion {
  id: string;
  text: string;
  timestamp: number;
  applied: boolean;
  contextScore: number;
}

export interface FieldContext {
  fieldName: string;
  displayName: string;
  fieldType: 'text' | 'number' | 'date' | 'email' | 'tel';
  isRequired: boolean;
  currentValue?: string;
  recentValues?: string[];
  priority: number;
}

export interface SuggestionContext {
  documentType?: 'utility_bill' | 'invoice' | 'statement';
  previousSelections?: { text: string; field: string; category: TextCategory }[];
  availableFields: FieldContext[];
  userPreferences?: Record<string, number>; // field preference scores
}

export const useSmartFieldSuggestions = () => {
  const [suggestions, setSuggestions] = useState<SmartSuggestion[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [appliedSuggestions, setAppliedSuggestions] = useState<SmartSuggestion[]>([]);
  const suggestionHistory = useRef<SmartSuggestion[]>([]);
  const { analyzeText } = useAITextRecognition();
  const { toast } = useToast();

  // Calculate field compatibility score
  const calculateFieldCompatibility = useCallback((
    fieldContext: FieldContext,
    suggestion: FieldSuggestion,
    textMetadata: any
  ): number => {
    let score = suggestion.confidence;
    
    // Field type compatibility
    if (fieldContext.fieldType === 'number' && textMetadata.isNumeric) {
      score += 0.2;
    } else if (fieldContext.fieldType === 'number' && !textMetadata.isNumeric) {
      score -= 0.3;
    }
    
    if (fieldContext.fieldType === 'date' && textMetadata.isDate) {
      score += 0.2;
    } else if (fieldContext.fieldType === 'date' && !textMetadata.isDate) {
      score -= 0.3;
    }
    
    if (fieldContext.fieldType === 'email' && textMetadata.hasEmail) {
      score += 0.3;
    } else if (fieldContext.fieldType === 'email' && !textMetadata.hasEmail) {
      score -= 0.4;
    }
    
    // Required field priority boost
    if (fieldContext.isRequired && !fieldContext.currentValue) {
      score += 0.1;
    }
    
    // Empty field priority
    if (!fieldContext.currentValue) {
      score += 0.05;
    } else {
      score -= 0.1; // Slight penalty for filled fields
    }
    
    // Field priority factor
    score += fieldContext.priority * 0.02;
    
    return Math.max(0, Math.min(1, score));
  }, []);

  // Generate contextual suggestions based on previous selections
  const generateContextualScore = useCallback((
    suggestion: FieldSuggestion,
    context: SuggestionContext
  ): number => {
    let contextScore = 0;
    
    // User preference boost
    if (context.userPreferences?.[suggestion.fieldName]) {
      contextScore += context.userPreferences[suggestion.fieldName] * 0.2;
    }
    
    // Previous selection patterns
    if (context.previousSelections) {
      const similarSelections = context.previousSelections.filter(
        sel => sel.category === suggestion.category
      );
      
      if (similarSelections.length > 0) {
        const fieldMatches = similarSelections.filter(
          sel => sel.field === suggestion.fieldName
        );
        
        if (fieldMatches.length > 0) {
          contextScore += 0.3; // Strong boost for consistent patterns
        } else {
          contextScore += 0.1; // Mild boost for category consistency
        }
      }
    }
    
    // Document type specific boosts
    if (context.documentType === 'utility_bill') {
      const utilityBoosts: Record<string, number> = {
        'amount': 0.15,
        'dueDate': 0.15,
        'accountNumber': 0.1,
        'serviceType': 0.1,
        'meterReading': 0.1
      };
      contextScore += utilityBoosts[suggestion.fieldName] || 0;
    }
    
    return Math.max(0, Math.min(1, contextScore));
  }, []);

  // Generate smart suggestions for selected text
  const generateSuggestions = useCallback(async (
    selectedText: string,
    context: SuggestionContext
  ): Promise<SmartSuggestion[]> => {
    if (!selectedText.trim() || context.availableFields.length === 0) {
      return [];
    }
    
    setIsGenerating(true);
    
    try {
      // Analyze the selected text
      const analysis = await analyzeText(selectedText, {
        includeMetadata: true,
        previousContext: context.previousSelections?.map(sel => sel.text)
      });
      
      // Generate enhanced suggestions
      const enhancedSuggestions: SmartSuggestion[] = [];
      
      // For each available field, calculate compatibility
      for (const fieldContext of context.availableFields) {
        // Find matching base suggestions
        const baseSuggestion = analysis.suggestions.find(
          s => s.fieldName === fieldContext.fieldName
        );
        
        if (baseSuggestion) {
          // Use existing suggestion as base
          const compatibilityScore = calculateFieldCompatibility(
            fieldContext,
            baseSuggestion,
            analysis.metadata
          );
          
          const contextScore = generateContextualScore(baseSuggestion, context);
          
          enhancedSuggestions.push({
            ...baseSuggestion,
            id: `suggestion-${Date.now()}-${fieldContext.fieldName}`,
            text: selectedText,
            timestamp: Date.now(),
            applied: false,
            confidence: compatibilityScore,
            contextScore
          });
        } else {
          // Create suggestion based on field compatibility
          const compatibilityScore = calculateFieldCompatibility(
            fieldContext,
            {
              fieldName: fieldContext.fieldName,
              confidence: 0.3,
              reasoning: 'Field type compatibility',
              category: 'unknown'
            },
            analysis.metadata
          );
          
          if (compatibilityScore > 0.2) {
            const contextScore = generateContextualScore(
              {
                fieldName: fieldContext.fieldName,
                confidence: compatibilityScore,
                reasoning: 'Field compatibility match',
                category: 'unknown'
              },
              context
            );
            
            enhancedSuggestions.push({
              id: `suggestion-${Date.now()}-${fieldContext.fieldName}`,
              fieldName: fieldContext.fieldName,
              confidence: compatibilityScore,
              reasoning: `Compatible with ${fieldContext.fieldType} field type`,
              category: analysis.category,
              text: selectedText,
              timestamp: Date.now(),
              applied: false,
              contextScore
            });
          }
        }
      }
      
      // Sort by combined score (confidence + contextScore)
      const sortedSuggestions = enhancedSuggestions
        .map(s => ({
          ...s,
          confidence: Math.min(1, s.confidence + s.contextScore * 0.3)
        }))
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 5); // Top 5 suggestions
      
      setSuggestions(sortedSuggestions);
      
      // Store in history
      suggestionHistory.current = [
        ...sortedSuggestions,
        ...suggestionHistory.current.slice(0, 95)
      ].slice(0, 100);
      
      return sortedSuggestions;
      
    } catch (error) {
      console.error('Error generating suggestions:', error);
      toast({
        title: 'Suggestion Error',
        description: 'Unable to generate field suggestions',
        variant: 'destructive'
      });
      return [];
    } finally {
      setIsGenerating(false);
    }
  }, [analyzeText, calculateFieldCompatibility, generateContextualScore, toast]);

  // Apply a suggestion
  const applySuggestion = useCallback((
    suggestionId: string,
    onApply?: (fieldName: string, text: string) => void
  ) => {
    const suggestion = suggestions.find(s => s.id === suggestionId);
    if (!suggestion) {
      console.warn('Suggestion not found:', suggestionId);
      return;
    }
    
    console.log('Applying suggestion:', suggestion);
    
    try {
      // Call the apply callback first
      onApply?.(suggestion.fieldName, suggestion.text);
      
      // Mark as applied
      const appliedSuggestion = { ...suggestion, applied: true };
      setAppliedSuggestions(prev => [appliedSuggestion, ...prev.slice(0, 19)]); // Keep last 20
      
      // Update the suggestion to show applied state instead of removing
      setSuggestions(prev => prev.map(s => 
        s.id === suggestionId ? appliedSuggestion : s
      ));
      
      toast({
        title: 'AI Suggestion Applied',
        description: `"${suggestion.text.slice(0, 30)}${suggestion.text.length > 30 ? '...' : ''}" → ${suggestion.fieldName}`,
        duration: 3000
      });
    } catch (error) {
      console.error('Error applying suggestion:', error);
      toast({
        title: 'Application Failed',
        description: 'Failed to apply suggestion. Please try again.',
        variant: 'destructive',
        duration: 3000
      });
    }
  }, [suggestions, toast]);

  // Get suggestion quality metrics
  const getSuggestionQuality = useCallback((suggestionId: string) => {
    const suggestion = suggestions.find(s => s.id === suggestionId);
    if (!suggestion) return null;
    
    const quality = {
      overall: suggestion.confidence,
      confidence: suggestion.confidence,
      contextRelevance: suggestion.contextScore,
      reasoning: suggestion.reasoning,
      category: suggestion.category
    };
    
    let qualityLabel = 'Low';
    if (quality.overall > 0.8) qualityLabel = 'Excellent';
    else if (quality.overall > 0.6) qualityLabel = 'Good';
    else if (quality.overall > 0.4) qualityLabel = 'Fair';
    
    return { ...quality, label: qualityLabel };
  }, [suggestions]);

  // Learn from user behavior
  const recordUserFeedback = useCallback((
    suggestionId: string,
    feedback: 'accepted' | 'rejected' | 'modified',
    finalValue?: string
  ) => {
    const suggestion = suggestions.find(s => s.id === suggestionId) ||
                     appliedSuggestions.find(s => s.id === suggestionId);
    
    if (!suggestion) return;
    
    // Store learning data for future improvements
    const learningData = {
      suggestionId,
      fieldName: suggestion.fieldName,
      originalText: suggestion.text,
      finalValue,
      feedback,
      confidence: suggestion.confidence,
      contextScore: suggestion.contextScore,
      timestamp: Date.now()
    };
    
    // Store in localStorage for persistence
    const stored = localStorage.getItem('smart-suggestions-learning');
    const learning = stored ? JSON.parse(stored) : [];
    learning.push(learningData);
    
    // Keep only last 1000 learning entries
    localStorage.setItem(
      'smart-suggestions-learning', 
      JSON.stringify(learning.slice(-1000))
    );
  }, [suggestions, appliedSuggestions]);

  // Clear suggestions
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return {
    suggestions,
    appliedSuggestions,
    isGenerating,
    generateSuggestions,
    applySuggestion,
    getSuggestionQuality,
    recordUserFeedback,
    clearSuggestions,
    suggestionHistory: suggestionHistory.current
  };
};
